/**
 * @fileoverview Header module for the Curtis Institute of Music WordPress theme.
 * This module manages complex header behaviors including:
 * - Dynamic secondary navigation menu creation based on scroll position
 * - Active menu item highlighting based on current scroll position
 * - Header compression states for better content visibility
 * - Smooth scroll handling with debouncing for performance
 * - WordPress admin bar offset calculations
 * - Module header visibility detection
 *
 * @module header
 */

import Headroom from 'headroom.js';
import debounce from '@/scripts/helpers/debounce';

/**
 * Header module interface
 * @interface Header
 */
type Header = {
  /** Headroom.js instance for header behavior */
  headroom: Headroom | null;
  /** DOM elements used by the header */
  elements: {
    /** Document body element */
    body: HTMLBodyElement | null;
    /** Site header element */
    header: HTMLElement | null;
    /** Site logo/branding element */
    logo: HTMLElement | null;
    /** Search container element */
    search: HTMLElement | null;
    /** Search toggle button */
    searchToggle: HTMLButtonElement | null;
    /** Sub-navigation menu from hero module */
    subnav: HTMLElement | null;
    /** Module header element to track */
    moduleHeader: HTMLElement | null;
    /** Array of page modules with IDs */
    modules: HTMLElement[];
    /** Secondary menu navigation links */
    links: HTMLAnchorElement[];
  };
  /** Last recorded scroll position */
  lastScrollPosition: number;
  /** Flag to prevent scroll handler overlap */
  isScrolling: boolean;
  /** Timeout ID for scroll debouncing */
  scrollTimeout: number | null;
  /** Timeout ID for transition completion */
  transitionTimeout: number | null;
  /** Timeout ID for secondary navigation transition suppression */
  navSyncTimeout: number | null;
  /** Initialize the header module */
  init: () => void;
  /** Bind event listeners */
  bindEvents: () => void;
  /** Handle scroll events */
  handleScroll: () => void;
  /** Toggle search visibility */
  handleSearchToggle: () => void;
  /** Add secondary navigation menu */
  handleAddSecondaryMenu: () => void;
  /** Remove secondary navigation menu */
  handleRemoveSecondaryMenu: () => void;
  /** Check if secondary menu overflows */
  checkForOverflow: () => void;
  /** Temporarily suppress header transitions while secondary nav is synced */
  suppressNavigationTransitions: () => void;
  /** Get the closest module to viewport top */
  getClosestModule: () => HTMLElement | null;
  /** Update active state of menu items */
  updateActiveMenuItem: () => void;
  /** Calculate header and admin bar offsets */
  calculateOffsets: () => { adminBar: number; header: number; total: number };
  /** Calculate the stable secondary navigation handoff point */
  calculateSecondaryNavigationThreshold: () => number;
  /** Check if module header is out of view */
  isModuleHeaderOutOfView: () => boolean;
  /** Scroll event handler reference */
  scrollHandler?: () => void;
  /** Toggle has-hero-feature header state while overlapping hero-feature */
  updateHeroFeatureHeaderState: () => void;
};

/**
 * Header module implementation that manages dynamic header behaviors
 * @type {Header}
 */
const header: Header = {
  headroom: null,
  scrollHandler: undefined,
  lastScrollPosition: 0,
  isScrolling: false,
  scrollTimeout: null,
  transitionTimeout: null,
  navSyncTimeout: null,

  /**
   * DOM elements used by the header module
   */
  elements: {
    body: document.querySelector('body'),
    header: document.querySelector('.site-header'),
    logo: document.querySelector('.site-branding'),
    search: document.querySelector('.site-search'),
    searchToggle: document.querySelector('.site-search [data-action="search-toggle"]'),
    subnav: document.querySelector('.module__hero .module__menu'),
    moduleHeader: document.querySelector('.module__header'),
    modules: Array.from(document.querySelectorAll('.module[id]:not([id=""])')),
    links: []
  },

  /**
   * Initializes the header module
   * Sets up event bindings and performs initial scroll check
   *
   * @returns {void}
   * @example
   * header.init(); // Initializes all header functionality
   */
  init() {
    this.bindEvents();
    this.updateHeroFeatureHeaderState();
  },

  /**
   * Calculates header and admin bar offsets for scroll calculations
   * Accounts for WordPress admin bar presence and responsive sizes
   *
   * @returns {{ adminBar: number; header: number; total: number }} Object containing offset values
   * @example
   * const offsets = header.calculateOffsets();
   * // Returns: { adminBar: 32, header: 98, total: 130 }
   */
  calculateOffsets() {
    // WordPress admin bar heights
    let adminBarHeight = 0;
    const adminBar = document.querySelector('#wpadminbar') as HTMLElement;

    if (adminBar && document.body.classList.contains('admin-bar')) {
      // WordPress admin bar is 32px on desktop, 46px on mobile
      adminBarHeight = window.innerWidth <= 999 ? 46 : 32;
    }

    // Header heights - normal: ~98px, compressed: ~58px
    let headerHeight = 98; // Default header height
    if (this.elements.header) {
      if (this.elements.header.classList.contains('headroom--not-top')) {
        headerHeight = 58; // Compressed header height
      } else {
        // Calculate actual header height
        headerHeight = this.elements.header.offsetHeight;
      }
    }

    return {
      adminBar: adminBarHeight,
      header: headerHeight,
      total: adminBarHeight + headerHeight
    };
  },

  /**
   * Binds event listeners for scroll, resize, and search toggle
   * Uses requestAnimationFrame for optimized scroll performance
   *
   * @returns {void}
   */
  bindEvents() {
    // Single consolidated scroll handler to prevent conflicts
    this.scrollHandler = () => {
      if (!this.isScrolling) {
        this.isScrolling = true;
        requestAnimationFrame(() => {
          this.handleScroll();
          this.isScrolling = false;
        });
      }
    };

    window.addEventListener('scroll', this.scrollHandler, { passive: true });
    window.addEventListener('resize', debounce(() => this.checkForOverflow(), 250));

    if (this.elements.searchToggle) {
      this.elements.searchToggle.addEventListener('click', () => this.handleSearchToggle());
    }

    // Initial check
    this.handleScroll();
  },

  /**
   * Calculates a stable viewport threshold for the secondary navigation handoff.
   *
   * The compressed header is shorter than the default header. If the handoff
   * uses the current header height, adding the sticky secondary menu can shrink
   * the threshold enough that the same scroll position immediately removes it
   * again. Keep the handoff tied to the expanded header height instead.
   *
   * @returns {number} Stable offset from the top of the viewport
   */
  calculateSecondaryNavigationThreshold() {
    const offsets = this.calculateOffsets();
    let expandedHeaderHeight = 98;

    if (
      this.elements.header &&
      !this.elements.header.classList.contains('headroom--not-top')
    ) {
      expandedHeaderHeight = this.elements.header.offsetHeight;
    }

    return offsets.adminBar + expandedHeaderHeight;
  },

  /**
   * Checks if the module header is completely out of view
   * Used to determine when to show the secondary navigation
   *
   * @returns {boolean} True if module header is above the viewport
   * @example
   * if (header.isModuleHeaderOutOfView()) {
   *   // Show secondary navigation
   * }
   */
  isModuleHeaderOutOfView() {
    if (!this.elements.moduleHeader) return false;

    const rect = this.elements.moduleHeader.getBoundingClientRect();
    const threshold = this.calculateSecondaryNavigationThreshold();
    const isSecondaryMenuVisible = !!document.querySelector('.site-secondary-menu');
    const removalBuffer = isSecondaryMenuVisible ? 8 : 0;

    // Use one expanded-header threshold for both states, with a small removal
    // buffer so minor scroll/rounding shifts do not bounce the cloned menu.
    return rect.bottom <= threshold + removalBuffer;
  },

  /**
   * Handles scroll events to manage header states and secondary navigation
   * Optimized with scroll difference threshold and debouncing
   *
   * @returns {void}
   */
  handleScroll() {
    let currentScrollPosition = 0;

    try {
      // Ensure hero-feature header state stays in sync
      this.updateHeroFeatureHeaderState();

      currentScrollPosition = window.pageYOffset || document.documentElement.scrollTop;

      // Validate scroll position is a number
      if (typeof currentScrollPosition !== 'number' || !isFinite(currentScrollPosition)) {
        console.warn('Invalid scroll position detected:', currentScrollPosition);
        return;
      }

      const scrollDifference = Math.abs(currentScrollPosition - this.lastScrollPosition);

      // Only process significant scroll changes to reduce layout thrashing
      if (scrollDifference < 5) {
        return;
      }
    } catch (error) {
      console.error('Error in header scroll handler:', error);
      return;
    }

    if (!this.elements.header) return;

    // Clear any existing transition timeout to prevent broken states
    if (this.transitionTimeout) {
      clearTimeout(this.transitionTimeout);
      this.transitionTimeout = null;
    }

    // Check if module header is completely out of view
    const moduleHeaderOutOfView = this.isModuleHeaderOutOfView();
    const shouldShowSecondaryNav = moduleHeaderOutOfView && this.elements.subnav;

    if (shouldShowSecondaryNav) {
      // Only add secondary menu if it doesn't already exist
      if (!document.querySelector('.site-secondary-menu')) {
        this.suppressNavigationTransitions();
        this.handleAddSecondaryMenu();
      }
      this.elements.body?.classList.add('header-not-top');
      this.elements.header.classList.add('headroom--not-top');
    } else {
      // Module header is still visible or partially visible
      if (document.querySelector('.site-secondary-menu')) {
        this.suppressNavigationTransitions();
      }
      this.handleRemoveSecondaryMenu();
      this.elements.body?.classList.remove('header-not-top');
      this.elements.header.classList.remove('headroom--not-top');
      this.elements.header.classList.remove('will-scroll');
    }

    // Update active menu item with debouncing to prevent excessive updates
    if (this.scrollTimeout) {
      clearTimeout(this.scrollTimeout);
    }

    this.scrollTimeout = window.setTimeout(() => {
      this.updateActiveMenuItem();
      this.scrollTimeout = null;
    }, 50);

    this.lastScrollPosition = currentScrollPosition;
  },

  /**
   * Toggles the search interface open/closed
   * Focuses the search input when opened for better UX
   *
   * @returns {void}
   * @example
   * header.handleSearchToggle(); // Opens search and focuses input
   */
  handleSearchToggle() {
    if (!this.elements.search) return;

    this.elements.search.classList.toggle('is-open');
    this.elements.search.querySelector('input')?.focus();
  },

  /**
   * Creates and adds the secondary navigation menu to the header
   * Clones the subnav content and manages transition states
   *
   * @returns {void}
   * @example
   * header.handleAddSecondaryMenu(); // Creates secondary nav when scrolled
   */
  handleAddSecondaryMenu() {
    if (!this.elements.logo || !this.elements.subnav) return;

    // Remove existing secondary menu if it exists
    this.handleRemoveSecondaryMenu();

    const menu = document.createElement('div');
    menu.classList.add('site-secondary-menu');
    menu.innerHTML = this.elements.subnav.innerHTML;
    this.elements.logo.insertAdjacentElement('afterend', menu);

    this.elements.links = Array.from(menu.querySelectorAll('a'));
    // Always set the overview (first link) as active when menu first appears
    const overview = this.elements.links[0];
    if (overview) overview.classList.add('active');
    this.checkForOverflow();

    // Re-check on the next frame after the compressed header styles settle.
    requestAnimationFrame(() => {
      this.checkForOverflow();
    });
  },

  /**
   * Removes the secondary navigation menu from the header
   * Cleans up timeouts and resets link references
   *
   * @returns {void}
   */
  handleRemoveSecondaryMenu() {
    const secondaryMenu = document.querySelector('.site-secondary-menu');
    if (secondaryMenu) {
      // Clear transition timeout when removing menu
      if (this.transitionTimeout) {
        clearTimeout(this.transitionTimeout);
        this.transitionTimeout = null;
      }

      secondaryMenu.remove();
    }
    this.elements.links = [];
  },

  /**
   * Checks if the secondary menu content overflows its container
   * Adds visual indicator class when scrolling is needed
   *
   * @returns {void}
   * @example
   * header.checkForOverflow(); // Adds 'will-scroll' class if menu overflows
   */
  checkForOverflow() {
    const secondaryMenu = document.querySelector('.site-secondary-menu');
    if (!secondaryMenu) return;

    if (secondaryMenu.scrollWidth > secondaryMenu.clientWidth) {
      this.elements.header?.classList.add('will-scroll');
    } else {
      this.elements.header?.classList.remove('will-scroll');
    }
  },

  /**
   * Temporarily disables header transitions while the sticky secondary nav is
   * inserted or removed. Without this, adding the secondary menu and then
   * measuring overflow causes the logo/menu toggle to animate through multiple
   * widths in consecutive frames, which reads as a main-nav flicker.
   *
   * @returns {void}
   */
  suppressNavigationTransitions() {
    if (!this.elements.header) return;

    this.elements.header.classList.add('site-header--nav-syncing');

    if (this.navSyncTimeout) {
      clearTimeout(this.navSyncTimeout);
    }

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.navSyncTimeout = window.setTimeout(() => {
          this.elements.header?.classList.remove('site-header--nav-syncing');
          this.navSyncTimeout = null;
        }, 50);
      });
    });
  },

  /**
   * Finds the module closest to the top of the viewport
   * Used for determining which section the user is viewing
   *
   * @returns {HTMLElement | null} The closest module element or null
   * @example
   * const currentModule = header.getClosestModule();
   * // Returns the module element nearest to viewport top
   */
  getClosestModule() {
    let closest: HTMLElement | null = null;
    let closestDistance = Infinity;

    this.elements.modules.forEach(module => {
      const distance = Math.abs(module.getBoundingClientRect().top);
      if (distance < closestDistance) {
        closest = module;
        closestDistance = distance;
      }
    });

    return closest;
  },

  /**
   * Updates the active menu item based on current scroll position
   * Highlights the menu item corresponding to the visible section
   * Handles edge cases like being at the top/bottom of the page
   *
   * @returns {void}
   * @example
   * header.updateActiveMenuItem(); // Highlights menu item for current section
   */
  updateActiveMenuItem() {
    if (this.elements.links.length === 0 || this.elements.modules.length === 0) return;

    const scrollPosition = window.scrollY;
    const windowHeight = window.innerHeight;
    const offsets = this.calculateOffsets();
    const totalOffset = offsets.total;

    let activeIndex = 0;

    // Add buffer to prevent flickering between sections
    const buffer = 50;

    // Check if we're at the bottom of the page - set last link as active
    if (scrollPosition + windowHeight >= document.documentElement.scrollHeight - 10) {
      activeIndex = this.elements.links.length - 1;
    } else {
      // Find the active module based on scroll position
      for (let i = 0; i < this.elements.modules.length; i++) {
        const module = this.elements.modules[i];
        const moduleTop = module.offsetTop - totalOffset - buffer;

        // If we're within this module's bounds or past its top
        if (scrollPosition >= moduleTop) {
          activeIndex = i + 1; // +1 because the first link is for the overview

          // If there's a next module and we haven't reached it yet, stay on current
          if (i < this.elements.modules.length - 1) {
            const nextModule = this.elements.modules[i + 1];
            const nextModuleTop = nextModule.offsetTop - totalOffset - buffer;
            if (scrollPosition < nextModuleTop) {
              break; // Stay on current module
            }
          }
        }
      }

      // If we're above the first section, set the overview (first link) as active
      const firstModuleTop = this.elements.modules[0].offsetTop - totalOffset - buffer;
      if (scrollPosition < firstModuleTop) {
        activeIndex = 0;
      }
    }

    // Ensure we don't exceed the available links
    activeIndex = Math.min(activeIndex, this.elements.links.length - 1);
    activeIndex = Math.max(activeIndex, 0);

    // Update active states - ensure only one is active at a time
    this.elements.links.forEach((link, index) => {
      const shouldBeActive = index === activeIndex;
      const isCurrentlyActive = link.classList.contains('active');

      if (shouldBeActive && !isCurrentlyActive) {
        link.classList.add('active');
      } else if (!shouldBeActive && isCurrentlyActive) {
        link.classList.remove('active');
      }
    });
  },

  /**
   * Toggle has-hero-feature header state when hero-feature is first and at top
   * @returns {void}
   */
  updateHeroFeatureHeaderState() {
    const hero = document.querySelector('.module--hero-feature') as HTMLElement | null;
    const contents = document.querySelector('#content .site-main__contents') as HTMLElement | null;

    if (!this.elements.header || !hero) {
      document.body.classList.remove('has-hero-feature');
      return;
    }

    // Only apply when the hero-feature is the first .module inside site-main__contents
    const firstModule = contents ? (contents.querySelector('.module') as HTMLElement | null) : null;
    const isFirst = firstModule === hero;

    if (!isFirst) {
      document.body.classList.remove('has-hero-feature');
      return;
    }

    const offsets = this.calculateOffsets();
    const headerTop = offsets.adminBar;
    const headerBottom = offsets.adminBar + offsets.header;

    // If the hero has a distinct content area, keep class until header reaches it
    const contentEl = hero.querySelector('.module__content') as HTMLElement | null;
    if (contentEl) {
      const contentRect = contentEl.getBoundingClientRect();
      const isBeforeContent = headerBottom <= contentRect.top; // header has not yet reached content

      if (isBeforeContent) {
        document.body.classList.add('has-hero-feature');
      } else {
        document.body.classList.remove('has-hero-feature');
      }
      return;
    }

    // Fallback: use intersection with the overall hero area
    const heroRect = hero.getBoundingClientRect();
    const isOverlayingHero = heroRect.bottom > headerTop && heroRect.top < headerBottom;

    if (isOverlayingHero) {
      document.body.classList.add('has-hero-feature');
    } else {
      document.body.classList.remove('has-hero-feature');
    }
  }
};

export default header;
